import type { ClinicalQuestionDetails } from '@/dashboard/doctor/clinical-question-details-view';
/**
 * Pure view logic for the patient-mode pre-consultation questionnaire
 * (PATIENT-PRE-CONSULTATION-QUESTIONNAIRE-AND-DOCTOR-HANDOFF-V1). No IO, no
 * React -- it maps the server-resolved question spec + the patient's in-progress
 * answer state into the bounded submit payload. The QUESTION SET is served by
 * the backend (org-resolved, authoritative), never hard-coded here.
 */

import type {
  PreconsultAnswerInput,
  PreconsultQuestionSpec,
  PreconsultResumeAnswer,
} from '@/dashboard/api';

export const PATIENT_QUESTIONNAIRE_TITLE = 'A few questions for the doctor';
/** ONE short help line (founder corrective on T-3: verified against the
 *  tablet-portrait line budget and pinned to it -- both meaning halves kept:
 *  patient-reported background + the doctor reviews). */
export const PATIENT_QUESTIONNAIRE_INTRO =
  'Your answers go to the doctor as background; the doctor reviews everything with you.';

/** One question's in-progress answer (one field is authoritative per kind). */
export interface QuestionAnswerState {
  /** Explicit Doctor draft clear; omission still preserves an existing answer. */
  clear?: true;
  /** Local Yes pending its explicit condition selection; never sent as a code. */
  conditionChoice?: 'yes' | 'no';
  code?: string;
  codes?: string[];
  text?: string;
  details?: ClinicalQuestionDetails | null;
}

/** All answers keyed by question key. */
export type QuestionnaireAnswerState = Record<string, QuestionAnswerState>;

/** Is a select option currently selected (single or multi)? */
export function isOptionSelected(
  answer: QuestionAnswerState | undefined,
  kind: PreconsultQuestionSpec['answerKind'],
  code: string,
): boolean {
  if (!answer || answer.clear) return false;
  if (kind === 'multi_select') return (answer.codes ?? []).includes(code);
  return answer.code === code;
}

/** Toggle a multi-select option in the answer state (immutable). */
export function toggleMultiOption(
  answer: QuestionAnswerState | undefined,
  code: string,
): QuestionAnswerState {
  const current = answer?.codes ?? [];
  const next = current.includes(code)
    ? current.filter((c) => c !== code)
    : [...current, code];
  return { ...(answer ?? {}), codes: next };
}

/** Whether a select question currently needs a free-text detail (an option
 *  with requiresDetail is selected). */
export function questionNeedsDetail(
  question: PreconsultQuestionSpec,
  answer: QuestionAnswerState | undefined,
): boolean {
  if (!answer || answer.clear) return false;
  const selected = new Set<string>();
  if (answer.code) selected.add(answer.code);
  for (const c of answer.codes ?? []) selected.add(c);
  return question.options.some((o) => o.requiresDetail && selected.has(o.code));
}

/** TRUE when a question has an answer worth submitting. */
export function isAnswered(
  question: PreconsultQuestionSpec,
  answer: QuestionAnswerState | undefined,
): boolean {
  if (!answer || answer.clear) return false;
  if (question.answerKind === 'short_text') {
    return (answer.text ?? '').trim().length > 0;
  }
  if (question.answerKind === 'multi_select') {
    return (answer.codes ?? []).length > 0;
  }
  return typeof answer.code === 'string' && answer.code.length > 0;
}

/**
 * Build the bounded submit payload from the answer state: one input per
 * ANSWERED question, shaped to the server's validatePreconsultAnswer contract
 * (single_select -> code [+ detail text]; multi_select -> codes [+ detail
 * text]; short_text -> text). Unanswered OPTIONAL questions are omitted;
 * unanswered REQUIRED questions block the single submit (the button gate +
 * the server's fail-closed required-completeness check).
 */
export function buildQuestionnairePayload(
  questions: readonly PreconsultQuestionSpec[],
  state: QuestionnaireAnswerState,
): PreconsultAnswerInput[] {
  const out: PreconsultAnswerInput[] = [];
  for (const q of questions) {
    const answer = state[q.key];
    if (answer?.clear) { out.push({ questionKey:q.key, clear:true }); continue; }
    if (!isAnswered(q, answer)) continue;
    const detail = (answer?.text ?? '').trim();
    if (q.answerKind === 'short_text') {
      out.push({ questionKey: q.key, text: detail });
      continue;
    }
    if (q.answerKind === 'multi_select') {
      const input: PreconsultAnswerInput = {
        questionKey: q.key,
        codes: [...(answer?.codes ?? [])],
      };
      if (questionNeedsDetail(q, answer) && detail.length > 0) input.text = detail;
      if (answer?.details !== undefined) input.details = answer.details !== null && (answer.text ?? '') === answer.details.legacyText ? answer.details : null;
      if (input.details != null) input.text = input.details.legacyText;
      out.push(input);
      continue;
    }
    // single_select
    const input: PreconsultAnswerInput = {
      questionKey: q.key,
      code: answer?.code,
    };
    if (questionNeedsDetail(q, answer) && detail.length > 0) input.text = detail;
    if (answer?.details !== undefined) input.details = questionNeedsDetail(q, answer) && answer.details !== null && (answer.text ?? '') === answer.details.legacyText ? answer.details : null;
    if (input.details != null) input.text = input.details.legacyText;
    out.push(input);
  }
  return out;
}

/** Required active questions still unanswered. Blocks the single submit: the
 *  digital journey may complete only after the required answers are entered
 *  (and then durably persisted server-side). */
export function unansweredRequired(
  questions: readonly PreconsultQuestionSpec[],
  state: QuestionnaireAnswerState,
): readonly PreconsultQuestionSpec[] {
  return questions.filter((q) => q.required && !isAnswered(q, state[q.key]));
}

/**
 * FAIL-CLOSED submit orchestration (founder corrective). The pre-consultation
 * answers are PERSISTED FIRST; the byte-unchanged completion engine runs ONLY
 * if that persistence succeeds. If `save` throws (a bounded retryable
 * persistence error, incl. 42P01), `complete` is NEVER called -- so a failed
 * digital questionnaire never silently produces intake completion, patient
 * linking, automatic check-in, or doctor-queue progression. When there is no
 * questionnaire (empty question set), completion proceeds unchanged.
 */
export async function saveThenComplete<T>(params: {
  readonly hasQuestionnaire: boolean;
  readonly save: () => Promise<unknown>;
  readonly complete: () => Promise<T>;
}): Promise<T> {
  if (params.hasQuestionnaire) {
    // Prove persistence BEFORE completion. A throw here propagates to the
    // caller's catch (patient-safe retry) and completion is not invoked.
    await params.save();
  }
  return params.complete();
}

/** Rehydrate the patient-mode answer state from the server RESUME payload so a
 *  re-entry reloads what already persisted -- the patient never re-answers a
 *  persisted question (the experience-floor law on retry). */
export function hydrateAnswerState(
  resumeAnswers: readonly PreconsultResumeAnswer[],
): QuestionnaireAnswerState {
  const state: QuestionnaireAnswerState = {};
  for (const a of resumeAnswers) {
    const entry: QuestionAnswerState = {};
    if (a.code !== null) entry.code = a.code;
    if (a.codes !== null && a.codes.length > 0) entry.codes = [...a.codes];
    if (a.text !== null) entry.text = a.text;
    if (a.details !== undefined) entry.details = a.details;
    state[a.questionKey] = entry;
  }
  return state;
}
